import { apiService } from '../api/api-client';
import { CashPool, CashPoolFormData, CashPoolProduct } from '@/types/cash-pool.types';

/**
 * Cash pool registry service (LR-580).
 *
 * Pools are never deleted - the backend exposes no DELETE; switching a pool off
 * is a PATCH of `is_active` (product answer #14).
 */

export interface CashPoolListParams {
  counterparty?: number;
  // The union, not `string`: a caller passing 'GMSLA' or 'repo' would type-check
  // and come back with an empty list, which the Trade Entry dropdown renders as
  // "no pool for this counterparty" - straight into a duplicate pool.
  product?: CashPoolProduct;
  currency?: string;
  /** Only pools in their agreement's currency: the ones a GMSLA booking accepts. */
  in_agreement_currency?: boolean;
  is_active?: boolean;
  limit?: number;
}

/**
 * One page, no paging loop.
 *
 * NOTE: ceiling — past `LIST_LIMIT` pools the table silently drops rows AND
 * `findDuplicatePool` starts missing duplicates, because it searches this same
 * list. A desk holds pools in the dozens, so the cap is far above the real
 * shape; `RegistryLimitOffsetPagination.max_limit` is 10000 if it ever needs
 * raising, and the real upgrade path is cursor pagination like the blotter's.
 */
const LIST_LIMIT = 1000;

/**
 * Payload sent to the API. Only the fields belonging to the selected rate shape
 * are included: the backend clears the others anyway, and omitting them keeps a
 * PATCH from resurrecting a stale value the form still holds.
 */
export const buildRatePayload = (data: CashPoolFormData) =>
  data.rate_type === 'floating'
    ? {
        rate_type: 'floating' as const,
        benchmark_name: data.benchmark_name,
        spread_bps: Number(data.spread_bps),
      }
    : {
        rate_type: 'fixed' as const,
        fixed_rate: data.fixed_rate,
      };

export const cashPoolService = {
  async getCashPools(params?: CashPoolListParams): Promise<CashPool[]> {
    const response = await apiService.getPaginated<CashPool>('/cash-pools/', {
      limit: LIST_LIMIT,
      ...params,
    });
    return response.results;
  },

  async createCashPool(data: CashPoolFormData): Promise<CashPool> {
    return apiService.post<CashPool>('/cash-pools/', {
      agreement: data.agreement,
      // Blank name is intentional: the backend generates
      // "CP · PRODUCT · CCY · rate definition".
      name: data.name.trim(),
      day_count_convention: data.day_count_convention,
      is_active: data.is_active,
      ...buildRatePayload(data),
    });
  },

  /**
   * Update a pool (PATCH). The agreement - and with it counterparty, currency and
   * product - is the pool identity and immutable server-side, so none is sent.
   */
  async updateCashPool(id: number, data: CashPoolFormData): Promise<CashPool> {
    return apiService.patch<CashPool>(`/cash-pools/${id}/`, {
      name: data.name.trim(),
      day_count_convention: data.day_count_convention,
      is_active: data.is_active,
      ...buildRatePayload(data),
    });
  },
};
